// HTTP-клиент API. Сессия — в HttpOnly cookie, поэтому токенов в коде нет.

import type { EventResult, LearningEvent, PhraseSet, Settings, Snapshot, Unit, UnitInput } from './types.ts';

export class ApiError extends Error {
  status: number;
  code: string;
  fields: Record<string, string>;
  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

/** Сеть недоступна или сервер не ответил. */
export class NetworkError extends Error {}

export function isUnauthorized(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

export interface Api {
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  snapshot(): Promise<Snapshot>;
  createUnit(u: UnitInput): Promise<Unit>;
  updateUnit(id: string, u: UnitInput): Promise<Unit>;
  /** Фраза для генерации: единица «готовится». */
  createDraft(form: string, sourceNote: string): Promise<Unit>;
  /** «Повторить» после сбоя и «Перегенерировать» на проверке. */
  generate(id: string): Promise<Unit>;
  approve(id: string): Promise<Unit>;
  /** Удаление неодобренной фразы. */
  deleteUnit(id: string): Promise<void>;
  /** Включение и выключение набора (идемпотентно). */
  setEnabled(id: string, on: boolean): Promise<PhraseSet>;
  updateSettings(s: Settings): Promise<Settings>;
  /** Подписка этого устройства на уведомления (PushSubscription.toJSON()). */
  subscribePush(sub: PushSubscriptionJSON): Promise<void>;
  /** Удаление подписки устройства (только своей). */
  unsubscribePush(endpoint: string): Promise<void>;
  /** Пробное уведомление на все устройства пользователя. */
  testPush(): Promise<{ delivered: number }>;
  sendEvents(events: LearningEvent[]): Promise<EventResult[]>;
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new NetworkError(String(e));
  }
  if (resp.status === 204) return undefined as T;
  let data: any = null;
  try {
    data = await resp.json();
  } catch {
    if (!resp.ok) throw new NetworkError(`HTTP ${resp.status}`);
  }
  if (!resp.ok) {
    if (resp.status >= 500 || !data) throw new NetworkError(`HTTP ${resp.status}`);
    throw new ApiError(resp.status, data.error ?? 'error', data.message ?? 'Ошибка', data.fields ?? {});
  }
  return data as T;
}

export const httpApi: Api = {
  login: (email, password) => call('POST', '/api/login', { email, password }),
  logout: () => call('POST', '/api/logout', {}),
  snapshot: () => call('GET', '/api/snapshot'),
  createUnit: (u) => call('POST', '/api/units', u),
  updateUnit: (id, u) => call('PUT', `/api/units/${encodeURIComponent(id)}`, u),
  createDraft: (form, sourceNote) => call('POST', '/api/units/draft', { form, sourceNote }),
  generate: (id) => call('POST', `/api/units/${encodeURIComponent(id)}/generate`, {}),
  approve: (id) => call('POST', `/api/units/${encodeURIComponent(id)}/approve`, {}),
  deleteUnit: (id) => call('DELETE', `/api/units/${encodeURIComponent(id)}`, {}),
  setEnabled: (id, on) => call('POST', `/api/sets/${encodeURIComponent(id)}/${on ? 'enable' : 'disable'}`, {}),
  updateSettings: (s) => call('PUT', '/api/settings', s),
  subscribePush: (sub) => call('POST', '/api/push/subscriptions', sub),
  unsubscribePush: (endpoint) => call('DELETE', '/api/push/subscriptions', { endpoint }),
  testPush: () => call('POST', '/api/push/test', {}),
  sendEvents: async (events) => (await call<{ results: EventResult[] }>('POST', '/api/events', { events })).results,
};
